import { Service } from "@lib/titan2-slingo/dist/api/Service";
import { AbstractGameView } from "@lib/titan2-slingo/dist/game/view/abstract/display/AbstractGameView";
import { TitanPanel } from "@lib/titan2/dist/game/control/TitanPanel";
import { TitanSpine } from "@lib/titan2/dist/game/control/TitanSpine";
import { TitanSprite } from "@lib/titan2/dist/game/control/TitanSprite";
import { TitanText } from "@lib/titan2/dist/game/control/TitanText";
import { OrientationType } from "@lib/titan2/dist/game/renderer/constant/RendererConstants";
import { EventObject } from "@lib/titan2/dist/util/event/EventObject";
import { RandomUtil } from "@lib/titan2/dist/util/random/RandomUtil";
import { MouseUtil } from "@lib/titan2/dist/util/view/MouseUtil";
import { SpineUtil } from "@lib/titan2/dist/util/view/SpineUtil";
import gsap from "gsap";
import { AdjustmentFilter } from "pixi-filters";
import { Graphics, Texture } from "pixi.js";
import { FishFoodType } from "../../../../../model/data/ChanceGamesData";
import { clearResponsiveSpineSlotsChildren as clearResponsiveSpineSlots, delayFor, getSpineSlot, getTitanSpineSlot, resetTitanSpine, skipToEnd, updateResponsiveSpineSlotsChildren } from "../../../../../utils/SpineUtils";
import { GameLang } from "../../../lang/GameLang";
import { Trail } from "../goldfish/Trail";
import { GameTailTotesSoundEvents } from "../../../../../controller/event/GameSountEvent";
const PanelSpineName = {
    [FishFoodType.Turtle]: "1-turtle",
    [FishFoodType.Green]: "2-green",
    [FishFoodType.Purple]: "3-purple",
    [FishFoodType.Red]: "4-red",
    [FishFoodType.Blue]: "5-blue",
    [FishFoodType.Gold]: "6-gold",
};
const PanelSpineSlotName = {
    [FishFoodType.Turtle]: "turtle",
    [FishFoodType.Green]: "green",
    [FishFoodType.Purple]: "purple",
    [FishFoodType.Red]: "red",
    [FishFoodType.Blue]: "blue",
    [FishFoodType.Gold]: "gold",
};
var FeatureFishFoodState;
(function (FeatureFishFoodState) {
    FeatureFishFoodState[FeatureFishFoodState["PICK_FOOD"] = 0] = "PICK_FOOD";
    FeatureFishFoodState[FeatureFishFoodState["PICK_TURTLE"] = 1] = "PICK_TURTLE";
})(FeatureFishFoodState || (FeatureFishFoodState = {}));
export class FeatureFishFood extends AbstractGameView {
    constructor(factory, landscapeProperties, portraitProperties) {
        super(factory, landscapeProperties, portraitProperties);
    }
    addEventListeners() {
        super.addEventListeners();
        this.registerGame(FeatureFishFoodEvents.FEATURE_PRESENT, this.featurePresent);
        this.registerGame(FeatureFishFoodEvents.SHOW, this.show);
        this.registerGame(FeatureFishFoodEvents.SET_PRIZE_VALUES, this.setPrizeValues);
        this.registerGame(FeatureFishFoodEvents.REVEAL_PICK, this.revealPick);
        this.registerGame(FeatureFishFoodEvents.DISABLE_PICKS, this.disablePicks);
        this.registerGame(FeatureFishFoodEvents.SHOW_MISSED_PICKS, this.showMissedPicks);
        this.registerGame(FeatureFishFoodEvents.CLEAR, this.clear);
        this.registerGame(FeatureFishFoodEvents.SHOW_TURTLES, this.showTurtles);
        this.registerGame(FeatureFishFoodEvents.REVEAL_TURTLE_PICK, this.revealTurtlePick);
    }
    create() {
        super.create();
        this.state = FeatureFishFoodState.PICK_FOOD;
        this.visible = false;
        this.alpha = 0;
        this.interactive = true;
        TitanSprite.createSprite(this, "background", this.landscapeProperties.background, this.portraitProperties?.background);
        TitanSprite.createSprite(this, "frame", this.landscapeProperties.frame, this.portraitProperties.frame);
        this.pickGridContainer = this.addChild(new AbstractGameView(this.factory, this.landscapeProperties.pickGridContainer, this.portraitProperties?.pickGridContainer));
        this.pickTurtleContainer = this.addChild(new AbstractGameView(this.factory, this.landscapeProperties.pickTurtleContainer, this.portraitProperties?.pickTurtleContainer));
        TitanSprite.createSprite(this, "total-win-background", this.landscapeProperties.totalWinBackground, this.portraitProperties.totalWinBackground);
        const totalWinTitle = TitanText.createField(this, "total-win-title", this.landscapeProperties.totalWinTitle, this.portraitProperties.totalWinTitle);
        this.totalWinValue = TitanText.createField(this, "total-win-value", this.landscapeProperties.totalWinValue, this.portraitProperties.totalWinValue);
        this.totalWinValue.pivot.y = this.totalWinValue.getHeight() / 2;
        this._lastWinAmount = 0;
        this.panelsSpine = TitanSpine.createSpine(this, "panels", this.landscapeProperties.panels, this.portraitProperties?.panels);
        this.panelValues = {};
        for (const key of Object.keys(FishFoodType)) {
            this.setPanelSpineState(0, FishFoodType[key]);
            const valueLandscape = TitanText.createField(null, key, this.landscapeProperties.panelValue);
            const valuePortrait = TitanText.createField(null, key, this.portraitProperties.panelValue);
            const winValueLandscape = TitanText.createField(null, key, this.landscapeProperties.panelWinValue);
            const winValuePortrait = TitanText.createField(null, key, this.portraitProperties.panelWinValue);
            this.panelValues[key] = { valueLandscape, valuePortrait, winValueLandscape, winValuePortrait };
            const landscapeTextSlot = getSpineSlot(this.panelsSpine.getSpine(OrientationType.LANDSCAPE), `dynamic-slot-landscape-${PanelSpineSlotName[key]}`);
            const portraitTextSlot = getSpineSlot(this.panelsSpine.getSpine(OrientationType.PORTRAIT), `dynamic-slot-portrait-${PanelSpineSlotName[key]}`);
            landscapeTextSlot.addChild(valueLandscape, winValueLandscape);
            portraitTextSlot.addChild(valuePortrait, winValuePortrait);
        }
        this.pickPanel = TitanPanel.createPanel(this.pickGridContainer, "pick-panel", this.landscapeProperties.pickPanel, this.portraitProperties.pickPanel);
        this.pickBubbles = [];
        for (let i = 0; i < 25; i++) {
            const itemsPerShelf = 5;
            const shelf = Math.floor(i / itemsPerShelf);
            const bubble = new BubblePick(this.factory, this.landscapeProperties.bubble, this.portraitProperties.bubble);
            const maxFrames = bubble.animation.getState().data.skeletonData.animations.find(animation => animation.name === "bag_dance_slow").duration * 30;
            bubble.animation.setSkin(`bag_${shelf}`);
            bubble.animation.gotoAndPlay(RandomUtil.nextInt(0, maxFrames), "bag_dance_slow", 0, true);
            bubble.animation.getSpine().update(0.01);
            this.pickPanel.addToPanel(bubble, false);
            this.addChild(bubble);
            this.pickBubbles.push(bubble);
            // bubble.hitArea = new Circle(0, 0, 65);
            MouseUtil.setInteractive(bubble, false);
            MouseUtil.addMouseDown(bubble, () => this.dispatchGame(new EventObject(FeatureFishFoodEvents.PICK_SELECTED.getType(), { index: i })), null);
        }
        this.pickPanel.arrange();
        this.turtles = [];
        this.turtleValues = [];
        for (let i = 0; i < 3; i++) {
            const turtle = TitanSpine.createSpine(this.pickTurtleContainer, `turtle-pick-${i}`, this.landscapeProperties.turtlePicks[i], this.portraitProperties.turtlePicks[i]);
            MouseUtil.setInteractive(turtle, false);
            MouseUtil.addMouseDown(turtle, () => this.dispatchGame(new EventObject(FeatureFishFoodEvents.TURTLE_PICK_SELECTED.getType(), { index: i })), null);
            this.turtles.push(turtle);
            this.turtleValues.push({
                pick: TitanText.createField(this, `turtle-pick-${i}`, this.landscapeProperties.turtleValues[i].pick, this.portraitProperties.turtleValues[i].pick),
                miss: TitanText.createField(this, `turtle-miss-${i}`, this.landscapeProperties.turtleValues[i].miss, this.portraitProperties.turtleValues[i].miss),
            });
            this.turtleValues[i].miss.alpha = 0.6;
        }
        this.helpText = TitanText.createField(this, "help-text", this.landscapeProperties.helpText, this.portraitProperties.helpText);
        TitanSprite.createSprite(this, "logo", this.landscapeProperties.logoProperties, this.portraitProperties?.logoProperties);
        this.helpButton = this.addChild(this.factory.createBonusHelp());
    }
    createBackgroundPanelTexture(width, height, colour, alpha) {
        const graphics = new Graphics();
        graphics.beginFill(colour, alpha);
        graphics.drawRect(0, 0, width, height);
        return Texture.from(this.renderer.getRenderer().generateTexture(graphics, { resolution: 1 }).baseTexture);
    }
    getPanelAnimName(level, type) {
        const base = `${PanelSpineName[type]}-active-${level}`;
        return [`landscape-${base}`, `portrait-${base}`];
    }
    getPanelLightSlotName(level, type) {
        return `bubble-${type.toLowerCase()}-${this.renderer.getOrientation().toLowerCase()}-${level}`;
    }
    getPanelAnimTrack(type) {
        return parseInt(PanelSpineName[type].split("-")[0]);
    }
    revealPick(event) {
        const payload = event.getPayload();
        const index = payload.index;
        const count = payload.count;
        const type = payload.type;
        const lastPick = payload.lastPick;
        const cumulativeWinAmount = payload.cumulativeWinAmount;
        const potAnimation = this.getPotAnimation(type);
        this.dispatchGame(GameTailTotesSoundEvents.PICK);
        const bubble = this.pickBubbles[index];
        SpineUtil.play(bubble.animation, potAnimation);
        if (lastPick) {
            this.helpText.setText("", true);
        }
        const bubblePrizeSlot = getSpineSlot(bubble.animation.getSpine(), `dynamic-item`);
        MouseUtil.setInteractive(bubble, false);
        bubble.interactiveChildren = false;
        bubble.interactive = false;
        const panelValues = [this.panelValues[type].valueLandscape, this.panelValues[type].valuePortrait];
        const panelWinValues = [this.panelValues[type].winValueLandscape, this.panelValues[type].winValuePortrait];
        const fish = TitanSprite.createSprite(bubblePrizeSlot, "fish-prize", this.landscapeProperties.items[type]);
        const timeline = gsap.timeline();
        const trailSpeed = 2;
        timeline.add(() => {
            const trail = new Trail(this.factory, this.landscapeProperties.respinTrailSpine, this.portraitProperties.respinTrailSpine);
            trail.setSpeed(trailSpeed);
            trail.bendAngle = 5;
            this.addChild(trail);
            this.setPanelSpineState(count, type);
            const lightslot = getTitanSpineSlot(this.panelsSpine, this.getPanelLightSlotName(count, type));
            const startPosition = bubble.parent.toGlobal(bubble.position);
            const endPosition = lightslot.parent.toGlobal(lightslot.position);
            const { complete, impact } = trail.play(startPosition, endPosition);
            this.dispatchGame(GameTailTotesSoundEvents.TRAIL_WOOSH);
            complete.then(() => {
                trail.destroy();
            });
            this.setPanelSpineState(count - 1, type);
            skipToEnd(this.panelsSpine, this.getPanelAnimTrack(type));
        }, `+=${0.66}`);
        timeline.add(() => {
            this.setPanelSpineState(count, type);
            const prizeSoundEvent = [null, GameTailTotesSoundEvents.PRIZE_LEVEL_1, GameTailTotesSoundEvents.PRIZE_LEVEL_2, GameTailTotesSoundEvents.PRIZE_LEVEL_3, GameTailTotesSoundEvents.PRIZE_LEVEL_4][count];
            this.dispatchGame(prizeSoundEvent);
        }, `+=${0.4 / trailSpeed}`);
        timeline.add(() => {
            if (type !== FishFoodType.Turtle && count >= 3) {
                panelWinValues.forEach(panelWinValue => {
                    gsap.to(panelWinValue, {
                        alpha: 1,
                        duration: 0.33,
                    });
                    gsap.to(panelWinValue, {
                        pixi: {
                            scale: 1.25
                        },
                        duration: 0.33,
                        ease: "sine.out",
                        yoyoEase: "back.out",
                        yoyo: true,
                        repeat: 1
                    });
                });
                panelValues.forEach(panelValue => {
                    gsap.to(panelValue, {
                        alpha: 0,
                        duration: 0.33,
                    });
                    gsap.to(panelValue, {
                        pixi: {
                            scale: 1.25
                        },
                        duration: 0.33,
                        ease: "sine.out",
                        yoyoEase: "back.out",
                        yoyo: true,
                        repeat: 1
                    });
                });
                gsap.delayedCall(0.33, () => {
                    const panelWinValue = this.renderer.isLandscape() ? this.panelValues[type].winValueLandscape : this.panelValues[type].winValuePortrait;
                    const clone = this.cloneTextInNewParent(panelWinValue, this);
                    clone.pivot.y = clone.getHeight() / 2;
                    clone.y += clone.pivot.y;
                    gsap.to(clone, {
                        pixi: {
                            scale: 2
                        },
                        duration: 0.8,
                        ease: "power1.out"
                    });
                    gsap.to(clone, {
                        pixi: {
                            scale: 0
                        },
                        delay: 0.8,
                        duration: 0.2,
                    });
                    gsap.to(clone, {
                        x: this.totalWinValue.x,
                        y: this.totalWinValue.y,
                        duration: 1,
                        ease: "power1.inOut",
                        onComplete: () => {
                            clone.destroy();
                        }
                    });
                    if (count === 4) {
                        const target = { value: this.panelValues[type].baseValue };
                        gsap.to(target, {
                            value: this.panelValues[type].baseValue * 2,
                            duration: 0.33,
                            onUpdate: () => {
                                panelWinValues.forEach(panelWinValue => {
                                    panelWinValue.setText(this.formatCurrency(target.value), true);
                                });
                            }
                        });
                    }
                });
            }
        }, `+=${0.33}`);
        timeline.add(() => this.rollupText(cumulativeWinAmount), `+=${0.9}`);
    }
    setPanelSpineState(count, type) {
        const animName = this.getPanelAnimName(count, type);
        const track = this.getPanelAnimTrack(type);
        this.panelsSpine.play(animName, false, false, track);
        this.panelsSpine.getSpine().update(0.01);
    }
    getPotAnimation(type) {
        return `bag_explode`;
    }
    disablePicks() {
        this.pickBubbles.forEach(bubble => {
            MouseUtil.setInteractive(bubble, false);
            if (bubble.animation.getSpine().state.getCurrent(0).animation.name === "bag_dance_slow") {
                SpineUtil.gotoAndStop(bubble.animation, 0, "bag_dance_slow");
            }
        });
    }
    featurePresent() {
        this.helpButton.setEnabled(!Service.isReplay());
        this.totalWinValue.setText(this.formatCurrency(0), true);
        for (const key of Object.keys(FishFoodType)) {
            this.setPanelSpineState(0, FishFoodType[key]);
        }
        for (const panel of Object.values(this.panelValues)) {
            panel.valueLandscape.alpha = 1;
            panel.valuePortrait.alpha = 1;
            panel.winValueLandscape.alpha = 0;
            panel.winValuePortrait.alpha = 0;
        }
        this.pickBubbles.forEach(bubble => {
            resetTitanSpine(bubble.animation);
            MouseUtil.setInteractive(bubble, false);
            bubble.visible = true;
            bubble.alpha = 1;
            bubble.filters = undefined;
            bubble.scale.set(1);
        });
        this.pickTurtleContainer.interactiveChildren = false;
        this.pickGridContainer.interactiveChildren = true;
        this.pickPanel.arrange();
        this.turtles.forEach(turtle => {
            MouseUtil.setInteractive(turtle, false);
            turtle.alpha = 0;
        });
        this.turtleValues.forEach(turtleValue => {
            turtleValue.pick.alpha = 0;
            turtleValue.miss.alpha = 0;
            turtleValue.pick.visible = false;
            turtleValue.miss.visible = false;
        });
        this.visible = true;
        this.alpha = 1;
        this.helpText.setLangKey(GameLang.BONUS_PROMPT3, true);
    }
    show() {
        gsap.delayedCall(1, () => {
            this.pickBubbles.forEach(bubble => {
                SpineUtil.playLoop(bubble.animation, "bag_dance_slow");
            });
            this.dispatchGame(FeatureFishFoodEvents.FOOD_PICK_READY);
            this.pickBubbles.forEach(bubble => {
                MouseUtil.setInteractive(bubble, !Service.isReplay());
                bubble.interactiveChildren = true;
                bubble.interactive = true;
            });
        });
    }
    setPrizeValues(event) {
        const payload = event.getPayload();
        const prizeMultipliers = payload.prizes;
        prizeMultipliers.forEach((prizeValue, type) => {
            if (type === "Turtle") {
                return;
            }
            const value = this.formatCurrency(prizeValue);
            this.panelValues[type].valueLandscape.setText(value, true);
            this.panelValues[type].valuePortrait.setText(value, true);
            this.panelValues[type].winValueLandscape.setText(value, true);
            this.panelValues[type].winValuePortrait.setText(value, true);
            this.panelValues[type].baseValue = prizeValue;
        });
    }
    showMissedPicks(event) {
        const missedPicks = event.getPayload().missedPicks;
        // map the missed types to an array with the type and count
        const revealOrder = [
            FishFoodType.Green,
            FishFoodType.Purple,
            FishFoodType.Blue,
            FishFoodType.Red,
            FishFoodType.Gold,
            FishFoodType.Turtle
        ].filter(type => missedPicks.has(type)).map(type => ({ type, count: missedPicks.get(type) }));
        // work out total number of reveals
        const totalReveals = revealOrder.reduce((total, type) => total + type.count, 0);
        // setup the reveal types array interleaving one of each type group, storing the index of each reveal
        const reveals = [];
        for (let i = 0; i < totalReveals; i++) {
            for (const grouping of revealOrder) {
                if (grouping.count > 0) {
                    reveals.push(grouping.type);
                    grouping.count--;
                }
            }
        }
        // map the interleaved types array to specific bubble picks, starting in the bottom right, and working toward top-left
        let searchFromIndex = this.pickBubbles.length - 1;
        const revealBubbles = reveals.map(type => {
            let bubble = undefined;
            for (let bubbleIndex = searchFromIndex; bubbleIndex >= 0; bubbleIndex--) {
                if (this.pickBubbles[bubbleIndex].animation.getSpine().state.getCurrent(0).animation.name === "bag_dance_slow") {
                    bubble = this.pickBubbles[bubbleIndex];
                    searchFromIndex = bubbleIndex - 1;
                    break;
                }
            }
            return { type, bubble };
        });
        // go through each grouping, revealing each groups fish in the order set out by the interleaved array, revealing every fish in each group at the same time, with a delay between the group
        const timeline = gsap.timeline();
        for (const fishType of revealOrder) {
            timeline.add(() => {
                const revealBubblesOfType = revealBubbles.filter(reveal => reveal.type === fishType.type);
                if (revealBubblesOfType.length === 0) {
                    return;
                }
                this.dispatchGame(GameTailTotesSoundEvents.BAG_REVEAL_MISS);
                revealBubblesOfType.forEach(({ type, bubble }) => {
                    const potAnimation = this.getPotAnimation(type);
                    const duration = SpineUtil.getDuration(bubble.animation, [potAnimation]);
                    SpineUtil.gotoAndPlay(bubble.animation, duration * 30, potAnimation);
                    const bubblePrizeSlot = getSpineSlot(bubble.animation.getSpine(), `dynamic-item`);
                    TitanSprite.createSprite(bubblePrizeSlot, "fish-prize", this.landscapeProperties.items[type]);
                    bubble.filters = [new AdjustmentFilter({ saturation: 0.1, brightness: 0.7, alpha: 0.9 })];
                });
            }, "+=0.5");
        }
        timeline.add(() => {
            this.dispatchGame(GameTailTotesSoundEvents.SYMBOLS_FADE);
            for (const bubble of this.pickBubbles) {
                gsap.to(bubble, {
                    pixi: {
                        scale: 0
                    },
                    duration: 0.66,
                    ease: "back.in(1)",
                    onComplete: () => {
                        // @ts-ignore
                        bubble.applyProperties();
                        bubble.visible = false;
                    }
                });
            }
        }, "+=1.5");
        timeline.add(() => {
            this.dispatchGame(FeatureFishFoodEvents.SHOW_MISSED_PICKS_COMPLETE);
        }, "+=1");
    }
    async showTurtles() {
        this.pickTurtleContainer.interactiveChildren = true;
        this.pickGridContainer.interactiveChildren = false;
        const goldTurtles = this.model.getFishFoodChanceGameData().isGoldTurtle();
        const idleAnim = goldTurtles ? "idle-gold" : "idle";
        this.state = FeatureFishFoodState.PICK_TURTLE;
        this.pickBubbles.forEach(bubble => {
            MouseUtil.setInteractive(bubble, false);
        });
        this.turtles.forEach((turtle, i, turtles) => {
            turtle.scale.set(0);
            turtle.alpha = 1;
            const maxFrames = turtle.getState().data.skeletonData.animations.find(animation => animation.name === idleAnim).duration * 30;
            SpineUtil.gotoAndPlay(turtle, (maxFrames / turtles.length / 3) * i, idleAnim, 0, true); // the /3 is because the idle does 3 loops inside it's animation, we want to stagger the idles to avoid them being in sync
        });
        this.dispatchGame(GameTailTotesSoundEvents.MOUSE_REVEAL);
        for (const turtle of this.turtles) {
            gsap.to(turtle, {
                pixi: {
                    scale: this.uiProperties.turtlePicks[0].scale.x
                },
                duration: 0.66,
                ease: "back.out(1)"
            });
        }
        await delayFor(0.1);
        this.turtles.forEach((turtle) => {
            MouseUtil.setInteractive(turtle, !Service.isReplay());
        });
        this.dispatchGame(FeatureFishFoodEvents.TURTLE_PICK_READY);
        this.helpText.setLangKey(GameLang.BONUS_PROMPT4, true);
    }
    revealTurtlePick(event) {
        this.helpButton.setEnabled(false);
        this.helpText.setText("", true);
        const goldTurtles = this.model.getFishFoodChanceGameData().isGoldTurtle();
        const danceAnim = goldTurtles ? "dance-gold" : "dance";
        const payload = event.getPayload();
        const index = payload.index;
        const pickValue = payload.pickValue;
        const missedValues = payload.missedValues;
        const totalWin = payload.totalWin;
        const pickedTurtle = this.turtles[index];
        SpineUtil.playLoop(pickedTurtle, danceAnim);
        this.dispatchGame(GameTailTotesSoundEvents.MOUSE_PICK);
        this.turtles.forEach((turtle) => {
            MouseUtil.setInteractive(turtle, false);
        });
        this.turtleValues.forEach(turtleValue => {
            turtleValue.pick.visible = true;
            turtleValue.miss.visible = true;
        });
        const pickedTurtleValueField = this.turtleValues[index].pick;
        pickedTurtleValueField.alpha = 1;
        pickedTurtleValueField.setText(this.formatCurrency(pickValue), true);
        pickedTurtleValueField.pivot.set(0, pickedTurtleValueField.getHeight() / 2);
        pickedTurtleValueField.scale.set(0);
        const missedTurtleValueFields = this.turtleValues.filter((_, i) => i !== index).map(turtleValue => turtleValue.miss);
        const timeline = gsap.timeline();
        timeline.add(gsap.to(pickedTurtleValueField, {
            pixi: {
                scale: 1,
            },
            ease: "back.out(4)",
            duration: 0.66
        }));
        timeline.add(() => {
            this.rollupText(totalWin);
        }, ">-0.1");
        timeline.add(gsap.delayedCall(0.5, () => { }));
        missedTurtleValueFields.forEach(missedTurtleValue => {
            missedTurtleValue.alpha = 0.6;
            missedTurtleValue.scale.set(0);
            missedTurtleValue.pivot.set(0, missedTurtleValue.getHeight() / 2);
            missedTurtleValue.setText(this.formatCurrency(missedValues.pop()), true);
            timeline.add(gsap.to(missedTurtleValue, {
                pixi: {
                    scale: 1,
                },
                ease: "back.out(4)",
                duration: 0.66
            }), "<0.1");
        });
    }
    rollupText(totalWin) {
        if (totalWin === this._lastWinAmount)
            return;
        this.dispatchGame(GameTailTotesSoundEvents.PRIZE_MONEY_COLLECT);
        const winAmount = { win: this._lastWinAmount };
        this._lastWinAmount = totalWin;
        gsap.to(winAmount, {
            win: totalWin,
            duration: 0.66,
            onUpdate: () => {
                this.totalWinValue.setText(this.formatCurrency(winAmount.win), true);
            },
            onComplete: () => {
                this.totalWinValue.setText(this.formatCurrency(totalWin), true);
            },
        });
        gsap.to(this.totalWinValue, {
            pixi: {
                scale: 1.2
            },
            duration: 0.33,
            yoyo: true,
            repeat: 1,
            ease: "circ.in",
            yoyoEase: "circ.out"
        });
    }
    clear() {
        this.state = FeatureFishFoodState.PICK_FOOD;
        this._lastWinAmount = 0;
        this.pickBubbles.forEach(bubble => {
            bubble.filters = undefined;
            clearResponsiveSpineSlots(bubble.animation, "dynamic-item");
            bubble.scale.set(1);
        });
        this.turtleValues.forEach(turtleValue => {
            turtleValue.pick.visible = false;
            turtleValue.miss.visible = false;
        });
        this.pickTurtleContainer.interactiveChildren = true;
        this.pickGridContainer.interactiveChildren = false;
        this.visible = false;
    }
    handleRotate(event) {
        super.handleRotate(event);
        this.pickBubbles.forEach(bubble => {
            updateResponsiveSpineSlotsChildren(bubble.animation, "dynamic-item");
        });
    }
    cloneTextInNewParent(object, parent) {
        const clone = object.clone();
        parent.addChild(clone);
        clone.position.copyFrom(parent.toLocal(object.parent.toGlobal(object.position)));
        clone.scale.set(1);
        clone.updateTransform();
        clone.scale.set(object.worldTransform.a / clone.worldTransform.a);
        clone.pivot.set(object.pivot.x, object.pivot.y);
        return clone;
    }
}
FeatureFishFood.REPLAY_TURTLE_PICK_INDEX = 1;
class BubblePick extends AbstractGameView {
    constructor(factory, landscapeProperties, portraitProperties) {
        super(factory, landscapeProperties, portraitProperties);
    }
    create() {
        this.bubble = TitanSpine.createSpine(this, this.landscapeProperties.animation, this.portraitProperties.animation);
    }
    get animation() {
        return this.bubble;
    }
    getWidth() {
        return this.uiProperties.size.x;
    }
    getHeight() {
        return this.uiProperties.size.x;
    }
}
export class FeatureFishFoodEvents {
}
FeatureFishFoodEvents.FEATURE_PRESENT = new EventObject("FeatureFishFoodEvents.FEATURE_PRESENT");
FeatureFishFoodEvents.SHOW = new EventObject("FeatureFishFoodEvents.SHOW");
FeatureFishFoodEvents.SET_PRIZE_VALUES = new EventObject("FeatureFishFoodEvents.SET_PRIZE_VALUES");
FeatureFishFoodEvents.PICK_SELECTED = new EventObject("FeatureFishFoodEvents.PICK_SELECTED");
FeatureFishFoodEvents.FOOD_PICK_READY = new EventObject("FeatureFishFoodEvents.FOOD_PICK_READY");
FeatureFishFoodEvents.TURTLE_PICK_READY = new EventObject("FeatureFishFoodEvents.TURTLE_PICK_READY");
FeatureFishFoodEvents.DISABLE_PICKS = new EventObject("FeatureFishFoodEvents.DISABLE_PICKS");
FeatureFishFoodEvents.REVEAL_PICK = new EventObject("FeatureFishFoodEvents.REVEAL_PICK");
FeatureFishFoodEvents.SHOW_MISSED_PICKS = new EventObject("FeatureFishFoodEvents.SHOW_MISSED_PICKS");
FeatureFishFoodEvents.SHOW_MISSED_PICKS_COMPLETE = new EventObject("FeatureFishFoodEvents.MISSED_PICKS_COMPLETE");
FeatureFishFoodEvents.SHOW_TURTLES = new EventObject("FeatureFishFoodEvents.SHOW_TURTLES");
FeatureFishFoodEvents.TURTLE_PICK_SELECTED = new EventObject("FeatureFishFoodEvents.TURTLE_PICK_SELECTED");
FeatureFishFoodEvents.REVEAL_TURTLE_PICK = new EventObject("FeatureFishFoodEvents.REVEAL_TURTLE_PICK");
FeatureFishFoodEvents.CLEAR = new EventObject("FeatureFishFoodEvents.CLEAR");
//# sourceMappingURL=FeatureFishFood.js.map